import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CheckCircle, Building2, MapPin, Mail, Hash } from "lucide-react";
import { useCompanyLogo } from "@/hooks/use-company-logo";

const onboardingSchema = z.object({
  name: z.string().min(1, "Företagsnamn krävs"),
  address: z.string().optional(),
  email: z.string().min(1, "E-postadress krävs").email("Ogiltig e-postadress"),
  orgNumber: z.string().min(1, "Organisationsnummer krävs"),
});

type OnboardingFormData = z.infer<typeof onboardingSchema>;

export default function ClientOnboarding() {
  const logoUrl = useCompanyLogo();
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<OnboardingFormData>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      name: "",
      address: "",
      email: "",
      orgNumber: "",
    },
  });

  const onSubmit = async (data: OnboardingFormData) => {
    setServerError(null);
    try {
      const response = await fetch("/api/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      const body = await response.json();
      if (!response.ok) {
        if (body.field === "email") {
          form.setError("email", { message: body.message });
        } else if (body.field === "orgNumber") {
          form.setError("orgNumber", { message: body.message });
        } else if (body.field === "name") {
          form.setError("name", { message: body.message });
        } else {
          setServerError(body.message || "Registrering misslyckades. Försök igen.");
        }
        return;
      }
      setDone(true);
    } catch {
      setServerError("Registrering misslyckades. Kontrollera din anslutning och försök igen.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src={logoUrl}
              alt="Rätt Order"
              className="h-16 w-auto max-w-[200px] object-contain"
              data-testid="img-onboarding-logo"
            />
          </div>
          <CardTitle className="text-2xl">Registrera ditt företag</CardTitle>
          <CardDescription>
            Fyll i uppgifterna nedan för att skapa ett konto och börja beställa.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {done ? (
            <div className="space-y-5" data-testid="section-credentials-result">
              <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-300">Registrering klar!</p>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    Vi har skickat ett mejl till din e-postadress med ditt användarnamn och en länk för att slutföra registreringen.
                    Länken gäller i 48 timmar. Kolla även skräpposten.
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                När du har följt länken kan du logga in på inloggningssidan.
              </p>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Företagsnamn</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            placeholder="Ditt företagsnamn"
                            className="pl-9"
                            data-testid="input-onboarding-name"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Adress <span className="text-muted-foreground font-normal">(valfri)</span></FormLabel>
                      <FormControl>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            placeholder="Gatuadress, postnummer, stad"
                            className="pl-9"
                            data-testid="input-onboarding-address"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-postadress</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            type="email"
                            placeholder="din@epost.se"
                            className="pl-9"
                            data-testid="input-onboarding-email"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="orgNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organisationsnummer</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Hash className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            placeholder="556000-0000"
                            className="pl-9"
                            data-testid="input-onboarding-orgnumber"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {serverError && (
                  <p className="text-sm text-destructive" data-testid="text-onboarding-error">
                    {serverError}
                  </p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                  data-testid="button-onboarding-submit"
                >
                  {form.formState.isSubmitting ? "Registrerar..." : "Skapa konto"}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
